import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import messageService from './messageService';
import './ConversationsList.css';

const ConversationsList = () => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const currentUser = JSON.parse(localStorage.getItem('user'));

  // Authentication check
  useEffect(() => {
    if (!currentUser || !localStorage.getItem('token')) {
      navigate('/login');
      return;
    }
  }, [currentUser, navigate]);

  // Load conversations
  useEffect(() => {
    const loadConversations = async () => {
      try {
        setLoading(true);
        const data = await messageService.getConversations();
        setConversations(data);
        // Artificial delay to ensure the loader is visible
        setTimeout(() => {
          setLoading(false);
        }, 1000);
      } catch (error) {
        console.error('Error loading conversations:', error);
        setError('Failed to load conversations');
        setLoading(false);
      }
    };

    loadConversations();
  }, []);

  const getOtherParticipant = (conversation) => {
    return conversation.participants.find(p => p.id !== currentUser.user.id);
  };

  const formatLastMessageTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    // Less than 24 hours
    if (diff < 24 * 60 * 60 * 1000) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    // Less than 7 days
    if (diff < 7 * 24 * 60 * 60 * 1000) {
      return date.toLocaleDateString([], { weekday: 'short' });
    }
    // Otherwise
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        zIndex: 9999
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '8px',
          margin: '40px 0'
        }}>
          <div style={{
            width: '15px',
            height: '15px',
            borderRadius: '50%',
            backgroundColor: '#1a237e',
            animation: 'propagate 1.5s ease-in-out infinite',
            transform: 'scale(0)'
          }}></div>
          <div style={{
            width: '15px',
            height: '15px',
            borderRadius: '50%',
            backgroundColor: '#1a237e',
            animation: 'propagate 1.5s ease-in-out infinite',
            transform: 'scale(0)',
            animationDelay: '0.15s'
          }}></div>
          <div style={{
            width: '15px',
            height: '15px',
            borderRadius: '50%',
            backgroundColor: '#1a237e',
            animation: 'propagate 1.5s ease-in-out infinite',
            transform: 'scale(0)',
            animationDelay: '0.3s'
          }}></div>
          <div style={{
            width: '15px',
            height: '15px',
            borderRadius: '50%',
            backgroundColor: '#1a237e',
            animation: 'propagate 1.5s ease-in-out infinite',
            transform: 'scale(0)',
            animationDelay: '0.45s'
          }}></div>
          <div style={{
            width: '15px',
            height: '15px',
            borderRadius: '50%',
            backgroundColor: '#1a237e',
            animation: 'propagate 1.5s ease-in-out infinite',
            transform: 'scale(0)',
            animationDelay: '0.6s'
          }}></div>
        </div>
        <p style={{
          marginTop: '20px',
          color: '#1a237e',
          fontSize: '16px',
          fontWeight: '500'
        }}>Loading conversations...</p>
        <style>{`
          @keyframes propagate {
            0%, 100% { transform: scale(0); }
            50% { transform: scale(1); }
          }
        `}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div className="conversations-container">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  return (
    <div className="conversations-container">
      <div className="conversations-header">
        <h1>Messages</h1>
        {currentUser.user.is_landlord && (
          <button 
            className="back-button"
            onClick={() => navigate('/landlord/dashboard')}
          >
            <i className="fas fa-arrow-left"></i> Back to Dashboard
          </button>
        )}
      </div>

      <div className="conversations-list">
        {conversations.length === 0 ? (
          <div className="no-conversations">
            No conversations yet
          </div>
        ) : (
          conversations.map(conversation => {
            const otherParticipant = getOtherParticipant(conversation);
            const lastMessage = conversation.last_message;

            return (
              <div 
                key={conversation.id}
                className="conversation-item"
                onClick={() => navigate(`/landlord/chat/${conversation.id}`)}
              >
                <div className="conversation-info">
                  <div className="participant-info">
                    <h3>{otherParticipant?.name || 'Unknown User'}</h3>
                    {conversation.unread_count > 0 && (
                      <span className="unread-badge">
                        {conversation.unread_count}
                      </span>
                    )}
                  </div>

                  {conversation.property && (
                    <div className="property-info">
                      <span className="property-type">
                        {conversation.property.property_type_display}
                      </span>
                      <span className="property-location">
                        {conversation.property.location}
                      </span>
                    </div>
                  )}

                  {lastMessage && (
                    <div className="last-message-container">
                      <p className="last-message">
                        {lastMessage.content.length > 50
                          ? `${lastMessage.content.substring(0, 50)}...`
                          : lastMessage.content}
                      </p>
                      <span className="message-time">
                        {formatLastMessageTime(lastMessage.timestamp)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default ConversationsList;